import React, { useEffect, useState } from "react";

import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/styles";
import { Card } from "../../components/Card";
import {
    TextField,
    Autocomplete,
    Button,
    Typography,
    Stack,
    Chip,
    Box,
    Snackbar,
    Alert,
    CircularProgress,
} from "@mui/material";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import useSWR from "swr";
import API_ENDPOINTS from "../../apis/endpoints";
import useAuth from "../../context";
import EmailPage from "./EmailPage";
import ImageUpload from "./ImageUpload";

function NewsLetterSend() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const auth = useAuth();
    const [recipientEmails, setRecipientEmails] = useState([]);
    const [senderEmail, setSenderEmail] = useState([]);
    const [ccEmails, setCcEmails] = useState([]);
    const [bccEmails, setBccEmails] = useState([]);
    const [templateName, setTemplateName] = useState("");
    const [subject, setSubject] = useState("");
    const [recipientError, setRecipientError] = useState("");
    const [ccError, setCcError] = useState("");
    const [bccError, setBccError] = useState("");
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });
    const storedDeviceId = localStorage.getItem("device-id");
    const [emailSent, setEmailSent] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    const handleUploadingChange = (uploading) => {
        setIsUploading(uploading);
    };

    const { data: templateData, isLoading: templateLoading } = useSWR(
        `${API_ENDPOINTS.NEWSLETTER}${id}/content/`,
        {
            revalidateOnFocus: false,
            revalidateOnMount: true,
            revalidateOnReconnect: false,
        }
    );

    useEffect(() => {
        if (templateData) {
            setTemplateName(templateData.name);
            setSubject(templateData.subject);
            setSenderEmail(templateData.sender);
            setRecipientEmails(templateData.recipients);
            setCcEmails(templateData.cc_recipients);
            setBccEmails(templateData.bcc_recipients);
        }
    }, [templateData]);

    const { data: sendersData, isLoading: sendersLoading } = useSWR(
        `${API_ENDPOINTS.NEWSLETTER}senders/`
    );

    const { data: recipientsData, isLoading: recipientsLoading } = useSWR(
        `${API_ENDPOINTS.NEWSLETTER}autocomplete/`
    );

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const validateEmails = (emails) => {
        return emails.filter((email) => !emailRegex.test(email));
    };

    const handleEmailChange = (setEmails, setError, value) => {
        const emailList = value.map((item) => {
            if (typeof item === "string") {
                return item;
            }
            return item.email || "";
        });

        const invalidEmails = validateEmails(emailList);
        if (invalidEmails.length > 0) {
            setError(`Invalid email(s): ${invalidEmails.join(", ")}`);
        } else {
            setError("");
        }
        setEmails(emailList);
    };

    const handleSenderSelection = (event, value) => {
        setSenderEmail(value?.email || "");
    };

    const handleDraftClick = async () => {
        try {
            const formData = new FormData();
            formData.append("name", templateName);
            formData.append("subject", subject);
            formData.append("sender", senderEmail);
            formData.append("recipients", recipientEmails);
            formData.append("cc_recipients", ccEmails);
            formData.append("bcc_recipients", bccEmails);
            formData.append("attachments", attachments.join(","));

            const response = await fetch(
                `${API_ENDPOINTS.NEWSLETTER}${id}/content/`,
                {
                    method: "PATCH",
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "x-cc-device-id": storedDeviceId,
                    },
                    body: formData,
                }
            );

            if (!response.ok) {
                throw new Error("Failed to save newsletter");
            }

            setSnackbar({
                open: true,
                message: "Draft saved successfully",
                severity: "success",
            });
        } catch (error) {
            console.error("Error saving template:", error);
            setSnackbar({
                open: true,
                message: "Failed to save draft",
                severity: "error",
            });
        }
    };

    const handleSendClick = async () => {
        if (!subject.trim()) {
            setSnackbar({
                open: true,
                message: "Please fill in the subject before sending",
                severity: "error",
            });
            return;
        }

        try {
            const formData = new FormData();
            formData.append("name", templateName);
            formData.append("subject", subject);
            formData.append("sender", senderEmail);
            formData.append("recipients", recipientEmails);
            formData.append("cc_recipients", ccEmails);
            formData.append("bcc_recipients", bccEmails);
            formData.append("attachments", attachments.join(","));

            const response = await fetch(
                `${API_ENDPOINTS.NEWSLETTER}${id}/content/`,
                {
                    method: "PATCH",
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "x-cc-device-id": storedDeviceId,
                    },
                    body: formData,
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(
                    errorData.message || "Failed to save newsletter"
                );
            }

            await handleSend();
            setSnackbar({
                open: true,
                message: "Newsletter sent successfully",
                severity: "success",
            });

            setEmailSent(true);

            setTimeout(() => {
                navigate("/dashboard/newsletter/list");
            }, 4000);
        } catch (error) {
            console.error("Error saving or sending newsletter:", error);
            setSnackbar({
                open: true,
                message: error.message || "Failed to send newsletter",
                severity: "error",
            });
        }
    };

    const handleSend = async () => {
        const response = await fetch(`${API_ENDPOINTS.NEWSLETTER}send/${id}/`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${auth.accessToken}`,
                "x-cc-device-id": storedDeviceId,
            },
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || "Failed to send newsletter");
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    if (templateLoading || sendersLoading || recipientsLoading) {
        return (
            <Box className="loader-container">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Card>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    marginBottom: "20px",
                    alignItems: "center",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        columnGap: "1rem",
                        alignItems: "center",
                    }}
                >
                    <ArrowBackIosNewRoundedIcon
                        onClick={() =>
                            navigate("/dashboard/newsletter/create?edit=" + id)
                        }
                    />

                    <Typography
                        variant="highlightEmphasis"
                        sx={{ color: theme.palette.neutral[200] }}
                    >
                        2/2
                    </Typography>
                    <Typography
                        variant="highlightEmphasis"
                        sx={{ color: theme.palette.neutral[500] }}
                    >
                        Send Newsletter{" "}
                    </Typography>
                </div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        columnGap: "1rem",

                        alignItems: "center",
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleDraftClick}
                        disabled={
                            ccError ||
                            bccError ||
                            recipientError ||
                            emailSent ||
                            isUploading
                        }
                    >
                        Save NewsLetter
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSendClick}
                        disabled={
                            ccError ||
                            bccError ||
                            recipientError ||
                            emailSent ||
                            isUploading
                        }
                    >
                        send
                    </Button>
                </div>
            </div>

            <div>
                <div
                    style={{
                        display: "flex",
                        width: "100%",
                        justifyContent: "space-between",
                        alignContent: "center",
                        marginBottom: "1rem",
                    }}
                >
                    <TextField
                        label="Template Name"
                        variant="outlined"
                        margin="normal"
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        sx={{ width: "49%" }}
                    />
                    <Autocomplete
                        options={sendersData || []}
                        getOptionLabel={(option) => option.name || option.email}
                        value={
                            sendersData.find(
                                (sender) => sender.email === senderEmail
                            ) || null
                        }
                        onChange={handleSenderSelection}
                        renderInput={(params) => (
                            <TextField {...params} label="Sender Email" />
                        )}
                        loading={sendersLoading}
                        sx={{ width: "49%", marginTop: "1rem" }}
                    />
                </div>

                <Autocomplete
                    multiple
                    freeSolo
                    options={recipientsData || []}
                    getOptionLabel={(option) => {
                        if (typeof option === "string") {
                            return option;
                        }
                        return option.email || "";
                    }}
                    onChange={(event, value) =>
                        handleEmailChange(
                            setRecipientEmails,
                            setRecipientError,
                            value
                        )
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Recipient Emails"
                            variant="outlined"
                            onChange={(event) => {
                                if (event.target.value.includes(",")) {
                                    const emails = event.target.value
                                        .split(",")
                                        .map((e) => e.trim());
                                    handleEmailChange(
                                        setRecipientEmails,
                                        setRecipientError,
                                        [...recipientEmails, ...emails]
                                    );
                                }
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props}>
                            <strong>{option.name}</strong> ({option.email})
                        </li>
                    )}
                    value={recipientEmails}
                    loading={recipientsLoading}
                    sx={{ marginBottom: "1.5rem" }}
                />

                {recipientError && (
                    <Typography
                        variant="body2"
                        color="error"
                        sx={{ marginTop: 1, marginBottom: 2 }}
                    >
                        {recipientError}
                    </Typography>
                )}

                <Autocomplete
                    multiple
                    freeSolo
                    options={recipientsData || []}
                    getOptionLabel={(option) => {
                        if (typeof option === "string") {
                            return option;
                        }
                        return option.email || "";
                    }}
                    onChange={(event, value) =>
                        handleEmailChange(setCcEmails, setCcError, value)
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="CC Emails"
                            variant="outlined"
                            onChange={(event) => {
                                if (event.target.value.includes(",")) {
                                    const emails = event.target.value
                                        .split(",")
                                        .map((e) => e.trim());
                                    handleEmailChange(setCcEmails, setCcError, [
                                        ...ccEmails,
                                        ...emails,
                                    ]);
                                }
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props}>
                            <strong>{option.name}</strong> ({option.email})
                        </li>
                    )}
                    value={ccEmails}
                    loading={recipientsLoading}
                    sx={{ marginBottom: "1.5rem" }}
                />
                {ccError && (
                    <Typography
                        variant="body2"
                        color="error"
                        sx={{ marginTop: 1, marginBottom: 2 }}
                    >
                        {ccError}
                    </Typography>
                )}

                <Autocomplete
                    multiple
                    freeSolo
                    options={recipientsData || []}
                    getOptionLabel={(option) => {
                        if (typeof option === "string") {
                            return option;
                        }
                        return option.email || "";
                    }}
                    onChange={(event, value) =>
                        handleEmailChange(setBccEmails, setBccError, value)
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="BCC Emails"
                            variant="outlined"
                            onChange={(event) => {
                                if (event.target.value.includes(",")) {
                                    const emails = event.target.value
                                        .split(",")
                                        .map((e) => e.trim());
                                    handleEmailChange(
                                        setBccEmails,
                                        setBccError,
                                        [...bccEmails, ...emails]
                                    );
                                }
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props}>
                            <strong>{option.name}</strong> ({option.email})
                        </li>
                    )}
                    value={bccEmails}
                    loading={recipientsLoading}
                    sx={{ marginBottom: "1.5rem" }}
                />
                {bccError && (
                    <Typography
                        variant="body2"
                        color="error"
                        sx={{ marginTop: 1, marginBottom: 2 }}
                    >
                        {bccError}
                    </Typography>
                )}

                <TextField
                    sx={{ marginBottom: "1.5rem" }}
                    fullWidth
                    label="Subject"
                    variant="outlined"
                    margin="normal"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                />
                <ImageUpload
                    attachments={templateData.attachments}
                    onChange={setAttachments}
                    onUploadingChange={handleUploadingChange}
                />
            </div>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Card>
    );
}

export default NewsLetterSend;
